import { Discipline, FooterLink, Phase, Software, UseCase, Version } from "@/services/global-services/models";
import { CommentEntryResponse, Element, Page } from "@/services/user-services/models";
import { ContentPage } from "@/services/user-services/models/ContentPage";
import { Dictionary } from "@/utils/Dictionary";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

type DataStore = {
    versions: Version[];
    setVersions: (versions: Version[]) => void;
    previewVersion: boolean;
    setPreviewVersion: (preview: boolean) => void;

    pages: ContentPage[];
    setPages: (pages: Page[]) => void;
    comments: Dictionary<CommentEntryResponse[]>;
    setComments: (pageSlug: string, comments: CommentEntryResponse[]) => void;

    elements: Dictionary<Element[]>;
    setElements: (version: string, elements: Element[]) => void;

    disciplines: Dictionary<Discipline[]>;
    phases: Dictionary<Phase[]>;
    software: Dictionary<Software[]>;
    useCases: Dictionary<UseCase[]>;
    classifications: Dictionary<Software[]>;
    setDisciplines: (version: string, disciplines: Discipline[]) => void;
    setSoftware: (version: string, software: Software[]) => void;
    setPhases: (version: string, phases: Phase[]) => void;
    setUseCases: (version: string, useCases: UseCase[]) => void;

    footerLinks: FooterLink[];
    setFooterLinks: (footerLinks: FooterLink[]) => void;
};

const useDataStore = create<DataStore>()(
    persist(
        (set) => ({
            // (set, get) => ({
            versions: [],
            setVersions: (versions: Version[]) => set({ versions }),
            previewVersion: false,
            setPreviewVersion: (preview: boolean) => {
                set((state) => {
                    const existingPreview = state.versions.find((v) => v.name === "wip");

                    if (existingPreview && !preview) {
                        // If we are disabling preview, remove the WIP version
                        return {
                            ...state,
                            previewVersion: false,
                            versions: state.versions.filter((v) => v.name !== "wip"),
                        };
                    }

                    if (existingPreview && preview) {
                        // If we are enabling preview and WIP already exists, just return the state
                        return { ...state, previewVersion: true };
                    }

                    return {
                        ...state,
                        previewVersion: preview,
                        versions: preview
                            ? [
                                  ...state.versions,
                                  { id: "wip", name: "wip", isDefault: false, publicationDate: new Date() },
                              ]
                            : state.versions.filter((v) => v.name !== "wip"),
                    };
                });
            },
            pages: [],
            setPages: (pages: Page[]) => {
                pages.forEach((page) => {
                    let url = page.slug;
                    let parent = pages.find((x) => x.id === page.parentPageId);

                    while (parent) {
                        url = `${parent.slug}/${url}`;
                        const parentId = parent.parentPageId;
                        parent = pages.find((x) => x.id === parentId);
                    }

                    page.url = url;
                });

                set({ pages });
            },
            comments: {},
            setComments: (pageSlug: string, comments: CommentEntryResponse[]) => {
                set((state) => ({
                    comments: { ...state.comments, [pageSlug]: comments },
                }));
            },
            elements: {},
            setElements: (version: string, elements: Element[]) =>
                set((state) => ({ elements: { ...state.elements, [version]: elements } })),
            disciplines: {},
            phases: {},
            software: {},
            useCases: {},
            classifications: {},
            setDisciplines: (version: string, disciplines: Discipline[]) =>
                set((state) => ({ disciplines: { ...state.disciplines, [version]: disciplines } })),
            setSoftware: (version: string, software: Software[]) =>
                set((state) => {
                    const actualSoftware = software.filter((x) => x.type === "DataStructure" || x.type === undefined);
                    const classifications = software.filter((x) => x.isClassification);

                    return {
                        software: { ...state.software, [version]: actualSoftware },
                        classifications: { ...state.classifications, [version]: classifications },
                    };
                }),
            setPhases: (version: string, phases: Phase[]) =>
                set((state) => ({ phases: { ...state.phases, [version]: phases } })),
            setUseCases: (version: string, useCases: UseCase[]) =>
                set((state) => ({ useCases: { ...state.useCases, [version]: useCases } })),
            footerLinks: [],
            setFooterLinks: (footerLinks: FooterLink[]) => set({ footerLinks }),
        }),
        {
            version: 1,
            name: "data-storage",
            storage: createJSONStorage(() => localStorage),
            partialize: (state) => ({ previewVersion: state.previewVersion }),
        }
    )
);

export default useDataStore;
